import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { allTerms, categories, searchTerms, getTermsByCategory } from '../data/glossary';
import './Glossary.css';

const CATEGORY_PILL_STYLES = {
  valuation:        { background: '#EFE6D0', color: '#B68D40' },
  profitability:    { background: '#EAF5E5', color: '#3B6D2A' },
  risk:             { background: '#FAE4E4', color: '#8E2B2B' },
  returns:          { background: '#F4F0E8', color: '#6F6A5E' },
  'market-concepts':{ background: '#EFE6D0', color: '#B68D40' },
  tax:              { background: '#FAF0D9', color: '#7A5A15' },
  products:         { background: '#EAF5E5', color: '#3B6D2A' },
  economics:        { background: '#FAF0D9', color: '#7A5A15' },
};

export default function Glossary() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTerms = useMemo(() => {
    if (query) return searchTerms(query);
    if (activeCategory !== 'all') return allTerms.filter(t => t.category === activeCategory);
    return allTerms;
  }, [query, activeCategory]);

  const groupedByCategory = useMemo(() => {
    if (query) {
      return [{ key: 'results', label: `Results for "${query}"`, terms: filteredTerms }];
    }
    if (activeCategory !== 'all') {
      const cat = categories.find(c => c.slug === activeCategory);
      return [{ key: activeCategory, label: cat?.name || activeCategory, terms: filteredTerms }];
    }
    return categories
      .map(cat => ({
        key: cat.slug,
        label: cat.name,
        terms: getTermsByCategory(cat.slug),
      }))
      .filter(g => g.terms.length > 0);
  }, [query, activeCategory, filteredTerms]);

  const pillStyle = (slug) => CATEGORY_PILL_STYLES[slug] || { background: '#F4F0E8', color: '#6F6A5E' };

  return (
    <div className="glossary-page">
      <Helmet>
        <title>Financial Glossary — DhanAcademy</title>
        <meta name="description" content="Every financial term, explained in plain English. No jargon. Indian examples. Free, always." />
      </Helmet>

      <section className="glossary-hero">
        <div className="glossary-hero-inner">
          <span className="glossary-hero-pill">GLOSSARY</span>
          <h1 className="glossary-hero-title">Every financial term, explained in plain English.</h1>
          <p className="glossary-hero-sub">
            Look up any term you've seen in the news, on your broker app, or in your tax form. No jargon. Indian examples. Free, always.
          </p>
          <div className="glossary-search-wrap">
            <input
              className="glossary-search"
              type="text"
              placeholder="Search for a term — e.g. P/E ratio, SIP, LTCG"
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveCategory('all'); }}
            />
          </div>
        </div>
      </section>

      <div className="glossary-body">
        <div className="glossary-inner">

          {!query && (
            <div className="glossary-pills-row">
              <button
                className={`glossary-pill ${activeCategory === 'all' ? 'glossary-pill-active' : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  className={`glossary-pill ${activeCategory === cat.slug ? 'glossary-pill-active' : ''}`}
                  onClick={() => setActiveCategory(cat.slug)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {groupedByCategory.length === 0 ? (
            <div className="glossary-empty">
              {query
                ? `No terms found for "${query}". Try a different search.`
                : 'No terms in this category yet.'}
            </div>
          ) : (
            groupedByCategory.map(group => (
              <section key={group.key} className="glossary-cat-section">
                <div className="glossary-cat-header">
                  <h2 className="glossary-cat-heading">{group.label}</h2>
                  <span className="glossary-cat-count">
                    {group.terms.length} {group.terms.length === 1 ? 'term' : 'terms'}
                  </span>
                </div>
                <div className="glossary-term-grid">
                  {group.terms.map(term => {
                    const ps = pillStyle(term.category);
                    return (
                      <Link key={term.slug} to={`/glossary/${term.slug}`} className="glossary-term-card">
                        <div className="glossary-term-top">
                          <span className="glossary-term-title">{term.title}</span>
                          <span className="glossary-term-pill" style={ps}>
                            {(categories.find(c => c.slug === term.category)?.name || term.category).toUpperCase()}
                          </span>
                        </div>
                        <p className="glossary-term-desc">{term.quickDefinition}</p>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))
          )}

        </div>
      </div>
    </div>
  );
}
